import React, { useEffect } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import useLocations from '@modules/organizationalStructure/locations/hooks/useLocations';

import { useRef } from 'react';

const LocationDropDowns = ({ register, setValue, data, formValues = {}, hideCountry = false }) => {
    const { t } = useTranslation();

    const {
        fetchCountries,
        fetchRegionsByCountry,
        fetchSubRegionsByRegion,
        fetchCitiesbySubRegion,
        countryOptions,
        regionOptions,
        subRegionOptions,
        cityOptions,
        selectedCountry,
        selectedRegion,
        selectedSubRegion,
        selectedCity,
        setSelectedCountry,
        setSelectedRegion,
        setSelectedSubRegion,
        setSelectedCity,
    } = useLocations();

    const initializedRef = useRef(false);

    useEffect(() => {
        fetchCountries();
    }, [fetchCountries]);

    // Set Bulgaria as default country when hideCountry is true
    useEffect(() => {
        let isMounted = true;

        const setDefaultCountry = async () => {
            if (hideCountry && !selectedCountry) {
                try {
                    await fetchCountries();
                    // Wait for countryOptions to be populated
                    if (countryOptions.length > 0 && isMounted) {
                        const bulgaria = countryOptions.find(option => option.value === 'BG');
                        if (bulgaria) {
                            setValue('country', bulgaria.value);
                            setSelectedCountry(bulgaria.value);
                            // Ensure we fetch regions after setting the country
                            await fetchRegionsByCountry(bulgaria.value);
                        }
                    }
                } catch (error) {
                    console.error('Error setting default country:', error);
                }
            }
        };

        setDefaultCountry();

        return () => {
            isMounted = false;
        };
    }, [hideCountry, countryOptions.length]);

    useEffect(() => {
        const initializeLocations = async () => {
            const addressInfo = data?.addressCityId;
            const formCountry = formValues?.country ?? null;
            const formRegion = formValues?.region ?? null;
            const formSubregion = formValues?.subregion ?? null;
            const formCity = formValues?.city ?? null;

            const countryId = addressInfo?.countryId ?? formCountry;
            const regionId = addressInfo?.regionId ?? formRegion;
            const subregionId = addressInfo?.subregionId ?? formSubregion;
            const cityId = addressInfo?.id ?? formCity;

            // Ensure countries are loaded
            if (countryOptions.length === 0) {
                return;
            }

            let resolvedCountry = countryId;

            if (!resolvedCountry) {
                const defaultCountry = countryOptions.find(option => option.value === 'BG');
                if (defaultCountry) {
                    resolvedCountry = defaultCountry.value;
                }
            }

            if (resolvedCountry) {
                if (selectedCountry !== resolvedCountry) {
                    setSelectedCountry(resolvedCountry);
                }
                if ((formCountry ?? '') !== resolvedCountry) {
                    setValue('country', resolvedCountry);
                }
                await fetchRegionsByCountry(resolvedCountry);
            }

            if (resolvedCountry && regionId) {
                if (selectedRegion !== regionId) {
                    setSelectedRegion(regionId);
                }
                if ((formRegion ?? null) !== regionId) {
                    setValue('region', regionId);
                }
                await fetchSubRegionsByRegion(regionId);
            } else {
                setSelectedRegion(null);
                setValue('region', null);
                setSelectedSubRegion(null);
                setValue('subregion', null);
                setSelectedCity(null);
                setValue('addressCityId', '');
                initializedRef.current = true;
                return;
            }

            if (subregionId) {
                if (selectedSubRegion !== subregionId) {
                    setSelectedSubRegion(subregionId);
                }
                if ((formSubregion ?? null) !== subregionId) {
                    setValue('subregion', subregionId);
                }
                await fetchCitiesbySubRegion(subregionId);
            } else {
                setSelectedSubRegion(null);
                setValue('subregion', null);
                setSelectedCity(null);
                setValue('addressCityId', '');
                initializedRef.current = true;
                return;
            }

            if (cityId) {
                if (selectedCity !== cityId) {
                    setSelectedCity(cityId);
                }
                if ((formCity ?? null) !== cityId) {
                    setValue('addressCityId', cityId);
                }
            } else {
                setSelectedCity(null);
                setValue('addressCityId', '');
            }

            initializedRef.current = true;
        };

        if (!initializedRef.current || formValues || data) {
            initializeLocations();
        }
    }, [
        data,
        formValues,
        hideCountry,
        fetchRegionsByCountry,
        fetchSubRegionsByRegion,
        fetchCitiesbySubRegion,
        countryOptions,
        setSelectedCountry,
        setSelectedRegion,
        setSelectedSubRegion,
        setSelectedCity,
        setValue
    ]);

    const customStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '38px',
            height: '38px',
            fontSize: '14px',
            padding: '0',
            borderColor: (state.isFocused ? '#2684ff' : base.borderColor),
            borderWidth: base.borderWidth,
            boxShadow: (state.isFocused ? base.boxShadow : 'none'),
            '&:hover': {
                borderColor: (state.isFocused ? '#2684ff' : '#b3b3b3'),
            },
        }),
        valueContainer: (base) => ({
            ...base,
            height: '38px',
            padding: '0 8px',
        }),
        input: (base) => ({
            ...base,
            margin: '0',
            padding: '0',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '38px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '0 4px',
        }),
    };

    return (
        <div className='location-dropdowns'>
            {!hideCountry && (
                <Select
                    // className="small-select"
                    name='country'
                    {...register('country')}
                    placeholder={t('nav.schools.form.country')}
                    options={countryOptions}
                    value={countryOptions.find(option => option.value === selectedCountry) || null}
                    onFocus={fetchCountries}
                    onChange={(selectedOption) => {
                        if (selectedOption) {
                            setValue('country', selectedOption.value);
                            setSelectedCountry(selectedOption.value);
                            setValue('region', null);
                            setValue('subregion', null);
                            setValue('addressCityId', "");
                            setSelectedRegion(null);
                            setSelectedSubRegion(null);
                            setSelectedCity("");
                            fetchRegionsByCountry(selectedOption.value);
                        }
                    }}
                    styles={customStyles}
                />
            )}
            <Select
                // className="small-select"
                name='region'
                {...register('region')}
                placeholder={t('persons.form.region')}
                options={regionOptions}
                value={regionOptions.find(option => option.value === selectedRegion) || null}
                isDisabled={!selectedCountry || regionOptions.length === 0}
                onChange={(selectedOption) => {
                    if (selectedOption) {
                        setValue('region', selectedOption.value);
                        setSelectedRegion(selectedOption.value);
                        setValue('subregion', null);
                        setValue('addressCityId', "");
                        setSelectedSubRegion(null);
                        setSelectedCity("");
                        fetchSubRegionsByRegion(selectedOption.value);
                    }
                }}
                styles={customStyles}
            />
            <Select
                // className="small-select"
                name='subregion'
                {...register('subregion')}
                placeholder={t('persons.form.municipality')}
                options={subRegionOptions}
                value={subRegionOptions.find(option => option.value === selectedSubRegion) || null}
                isDisabled={!selectedRegion || subRegionOptions.length === 0}
                onChange={(selectedOption) => {
                    if (selectedOption) {
                        setValue('subregion', selectedOption.value);
                        setSelectedSubRegion(selectedOption.value);
                        setValue('addressCityId', "");
                        setSelectedCity("");
                        fetchCitiesbySubRegion(selectedOption.value);
                    }
                }}
                styles={customStyles}
            />
            <Select
                // className="small-select"
                name='city'
                {...register('addressCityId')}
                placeholder={t('persons.form.city')}
                options={cityOptions}
                value={cityOptions.find(option => option.value === selectedCity) || null}
                isDisabled={!selectedSubRegion || cityOptions.length === 0}
                onChange={(selectedOption) => {
                    if (selectedOption) {
                        setValue('addressCityId', selectedOption.value);
                        setSelectedCity(selectedOption.value);
                    }
                }}
                styles={customStyles}
            />
        </div>
    );
};

export default LocationDropDowns;